// src/Dashboard/pages/user/Messaging/MessagingDashboard.jsx

import React from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import useScrollToTop from "../../../../hooks/useScollToTop";

import { useMessagingDashboard } from "./hooks/useMessagingDashboard";

import MessagingHeader from "./components/MessagingHeader";
import MessagingKpis from "./components/MessagingKpis";
import MessagingModuleNav from "./components/MessagingModuleNav";
import PrivateMessagesPanel from "./components/PrivateMessagesPanel";
import MessageReadDrawer from "./components/MessageReadDrawer";
import ComposeMessageDrawer from "./components/ComposeMessageDrawer";
import ComposeGroupMessageDrawer from "./components/ComposeGroupMessageDrawer";
import ChatChannelsPanel from "./components/ChatChannelsPanel";

export default function MessagingDashboard() {
  useScrollToTop();

  const messaging = useMessagingDashboard();

  const {
    userId,
    userRole,
    isManager,

    activeModule,
    setActiveModule,

    receivedMessages,
    sentMessages,
    loadingReceived,
    loadingSent,
    unreadCount,
    busyAllRead,

    composePrivateOpen,
    setComposePrivateOpen,
    composeGroupOpen,
    setComposeGroupOpen,

    readMessage,
    readMessageType,
    openReadMessage,
    closeReadMessage,

    replyToMessage,
    openReplyDrawer,
    closeReplyDrawer,

    deletePrivateMessage,
    markReceivedAsRead,
    markReceivedAsUnreadLocal,
    handleMarkAllAsRead,

    handlePrivateMessageSent,
    handleGroupMessageSent,

    channels,
    selectedChannel,
    selectedChannelId,
    setSelectedChannelId,
    loadingChannels,

    channelMessages,
    loadingChannelMessages,
    sendingChannelMessage,
    sendChannelMessage,
    deleteChannelMessage,
    refreshChannelMessages,
  } = messaging;

  return (
    <section className="w-full pb-10 pt-5 font-satoshi">
      <div className="mx-auto max-w-[1900px] space-y-5 px-3 sm:px-4 lg:px-6">
        <MessagingHeader
          isManager={isManager}
          unreadCount={unreadCount}
          onComposePrivate={() => setComposePrivateOpen(true)}
          onComposeGroup={() => setComposeGroupOpen(true)}
        />

        <MessagingKpis
          receivedCount={receivedMessages.length}
          sentCount={sentMessages.length}
          unreadCount={unreadCount}
          channelsCount={channels.length}
          loading={loadingReceived || loadingSent || loadingChannels}
        />

        <section className="grid gap-5 lg:grid-cols-[280px_minmax(0,1fr)]">
          <MessagingModuleNav
            activeModule={activeModule}
            onChange={setActiveModule}
            isManager={isManager}
            unreadCount={unreadCount}
            receivedCount={receivedMessages.length}
            sentCount={sentMessages.length}
            channelsCount={channels.length}
          />

          <div className="min-w-0">
            {activeModule === "received" && (
              <PrivateMessagesPanel
                type="received"
                title="Messages reçus"
                subtitle="Boîte de réception personnelle."
                messages={receivedMessages}
                loading={loadingReceived}
                unreadCount={unreadCount}
                busyAllRead={busyAllRead}
                onOpen={(message) => openReadMessage(message, "received")}
                onReply={openReplyDrawer}
                onDelete={deletePrivateMessage}
                onMarkRead={markReceivedAsRead}
                onMarkUnread={markReceivedAsUnreadLocal}
                onMarkAllRead={handleMarkAllAsRead}
              />
            )}

            {activeModule === "sent" && (
              <PrivateMessagesPanel
                type="sent"
                title="Messages envoyés"
                subtitle="Historique des messages que vous avez envoyés."
                messages={sentMessages}
                loading={loadingSent}
                onOpen={(message) => openReadMessage(message, "sent")}
                onDelete={deletePrivateMessage}
              />
            )}

            {activeModule === "channels" && (
              <ChatChannelsPanel
                isManager={isManager}
                userId={userId}
                channels={channels}
                selectedChannel={selectedChannel}
                selectedChannelId={selectedChannelId}
                setSelectedChannelId={setSelectedChannelId}
                loadingChannels={loadingChannels}
                messages={channelMessages}
                loadingMessages={loadingChannelMessages}
                sending={sendingChannelMessage}
                onSend={sendChannelMessage}
                onDelete={deleteChannelMessage}
                onRefresh={refreshChannelMessages}
              />
            )}
          </div>
        </section>
      </div>

      <MessageReadDrawer
        isOpen={!!readMessage}
        type={readMessageType}
        message={readMessage}
        onClose={closeReadMessage}
        onReply={openReplyDrawer}
        onDelete={deletePrivateMessage}
      />

      <ComposeMessageDrawer
        isOpen={composePrivateOpen}
        userId={userId}
        userRole={userRole}
        onClose={() => setComposePrivateOpen(false)}
        onSend={handlePrivateMessageSent}
      />

      <ComposeMessageDrawer
        isOpen={!!replyToMessage}
        userId={userId}
        userRole={userRole}
        mode="reply"
        initialRecipientId={replyToMessage?.sender_id}
        initialSubject={
          replyToMessage?.subject?.startsWith("RE:")
            ? replyToMessage?.subject
            : `RE: ${replyToMessage?.subject || ""}`
        }
        lockedRecipient={replyToMessage?.Sender}
        onClose={closeReplyDrawer}
        onSend={handlePrivateMessageSent}
      />

      {isManager && (
        <ComposeGroupMessageDrawer
          isOpen={composeGroupOpen}
          onClose={() => setComposeGroupOpen(false)}
          onSend={handleGroupMessageSent}
        />
      )}

      <ToastContainer />
    </section>
  );
}
